import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonIcon,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonAlert,
  IonCheckbox,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { checkmarkCircleOutline } from 'ionicons/icons';
import { apiCall } from '@/util/api';
import { useParams } from 'react-router-dom';
import { useUserContext } from '@/modules/user/useUserContext';
import SnowballMenu from '@/modules/user/SnowballMenu';
import { TopicLogic } from './logic';
import type { Topic, Registration } from './types';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { useActionGuard } from '@/hooks/useActionGuard';
import { useAppToast } from '@/hooks/useAppToast';
import { pxToRem } from '@/modules/config/constants';

interface RegisterResponse {
  success: boolean;
  message: string;
  user?: {
    id: string;
    email?: string;
    phoneTail: string;
  };
  topic?: Topic;
}

const TopicRegistration: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const userContext = useUserContext();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [unregistering, setUnregistering] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showUnregisterAlert, setShowUnregisterAlert] = useState(false);
  const [presentToast] = useAppToast();
  const [smsReminder, setSmsReminder] = useState(false);
  const [attendanceLikelihood, setAttendanceLikelihood] = useState<
    string | undefined
  >(undefined);

  const prevUserContextRef = useRef<{
    userId?: string;
    claims: string;
  } | null>(null);

  const fetchTopicInfo = useCallback(async () => {
    if (!topicId) return;
    try {
      const encodedTopicId = encodeURIComponent(topicId);
      const response = await apiCall(`/api/topics/${encodedTopicId}`);
      if (response.ok) {
        const data = await response.json();
        setTopic(TopicLogic.fromJSON(data));
      }
    } catch {
      // Silently fail - topic info is supplementary here
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  const checkRegistration = useCallback(async () => {
    if (!topicId) return;
    try {
      const encodedTopicId = encodeURIComponent(topicId);
      const response = await apiCall(
        `/api/topic-registrations?topic_id=${encodedTopicId}`
      );
      if (response.ok) {
        const data: Registration | null = await response.json();
        setRegistration(data);
        if (data) {
          setAttendanceLikelihood(
            data.attendanceLikelihood != null
              ? String(data.attendanceLikelihood)
              : undefined
          );
          setSmsReminder(data.smsReminder);
        }
      } else {
        setRegistration(null);
      }
    } catch {
      setRegistration(null);
    }
  }, [topicId]);

  useIonViewWillEnter(() => {
    if (topicId) {
      fetchTopicInfo();
      checkRegistration();
    }
  });

  // Rehydrate on mount as well. On a hard load / reload that lands directly on
  // this route, Ionic-React does not fire the view-enter lifecycle for the
  // initial page, so the useIonViewWillEnter above never runs and `loading`
  // would stay true. Fetching here ensures a direct load populates the form.
  useEffect(() => {
    if (topicId) {
      fetchTopicInfo();
      checkRegistration();
    }
  }, [topicId, fetchTopicInfo, checkRegistration]);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Re-check registration when UserContext changes (after verification)
  useEffect(() => {
    const currentUserContext = {
      userId: userContext.userId,
      claims: JSON.stringify(userContext.claims),
    };
    if (prevUserContextRef.current === null) {
      prevUserContextRef.current = currentUserContext;
      return;
    }
    const prev = prevUserContextRef.current;
    if (
      prev.userId !== currentUserContext.userId ||
      prev.claims !== currentUserContext.claims
    ) {
      if (topicId) {
        checkRegistration();
      }
    }
    prevUserContextRef.current = currentUserContext;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext]);

  const handleRegisterForTopic = async () => {
    setRegistering(true);
    try {
      const response = await apiCall('/api/topic-registrations', {
        method: 'POST',
        body: JSON.stringify({
          topicId,
          attendanceLikelihood: attendanceLikelihood
            ? parseFloat(attendanceLikelihood)
            : null,
          smsReminder,
        }),
      });
      const data: RegisterResponse = await response.json();
      if (response.ok && data.success) {
        setShowSuccessAlert(true);
        await checkRegistration();
      } else {
        presentToast({
          message: data.message || 'Failed to register for topic',
          color: 'danger',
          duration: 3000,
        });
      }
    } catch {
      presentToast({
        message: 'Network error occurred',
        color: 'danger',
        duration: 3000,
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleUpdateRegistration = async () => {
    if (!registration) return;
    setUpdating(true);
    try {
      const response = await apiCall(
        `/api/topic-registrations/${registration.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            attendanceLikelihood: attendanceLikelihood
              ? parseFloat(attendanceLikelihood)
              : null,
            smsReminder,
          }),
        }
      );
      if (response.ok) {
        presentToast({
          message: 'Registration updated',
          color: 'success',
          duration: 3000,
        });
        await checkRegistration();
      } else {
        const data = await response.json().catch(() => ({}));
        presentToast({
          message: data.message || 'Failed to update registration',
          color: 'danger',
          duration: 3000,
        });
      }
    } catch {
      presentToast({
        message: 'Network error occurred',
        color: 'danger',
        duration: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleUnregister = async () => {
    if (!registration) return;
    setUnregistering(true);
    try {
      const response = await apiCall(
        `/api/topic-registrations/${registration.id}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setRegistration(null);
        setAttendanceLikelihood(undefined);
        setSmsReminder(false);
        presentToast({
          message: "You've been unregistered",
          color: 'success',
          duration: 3000,
        });
      } else {
        const data = await response.json().catch(() => ({}));
        presentToast({
          message: data.message || 'Failed to unregister',
          color: 'danger',
          duration: 3000,
        });
      }
    } catch {
      presentToast({
        message: 'Network error occurred',
        color: 'danger',
        duration: 3000,
      });
    } finally {
      setUnregistering(false);
    }
  };

  const primaryAction = registration ? ACTIONS.UPDATE : ACTIONS.CREATE;
  const primaryResourceId = registration ? String(registration.id) : undefined;
  const { canPerformAction: canSubmit } = useActionGuard(
    primaryAction,
    RESOURCES.TOPIC_REGISTRATIONS,
    {
      singleVerification: 'Verify yourself to register for this topic',
      updateProfile: 'Update your profile to register for this topic',
      setupProfile: 'Set up your profile to register for this topic',
    },
    primaryResourceId
  );

  const formatDate = (dateString: string | undefined) => {
    try {
      if (!dateString) return 'recently';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'recently';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'recently';
    }
  };

  return (
    <IonPage data-testid="register-for-topic-page">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/topics/${topicId}`} />
          </IonButtons>
          <IonTitle>Register for Topic</IonTitle>
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <div style={{ padding: '20px' }}>
            {topic && (
              <h2 style={{ fontWeight: '600', marginBottom: '8px' }}>
                {topic.title}
              </h2>
            )}

            {registration && (
              <p
                style={{
                  fontSize: pxToRem(16),
                  marginBottom: '16px',
                }}
              >
                <IonIcon
                  icon={checkmarkCircleOutline}
                  color="success"
                  style={{ marginRight: '8px', verticalAlign: 'middle' }}
                />
                You registered on{' '}
                <strong>{formatDate(registration.registeredAt)}</strong>
              </p>
            )}

            <ActionGuardContainer>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  columnGap: '12px',
                  rowGap: '16px',
                  alignItems: 'center',
                  padding: '0 4px',
                  marginBottom: '16px',
                }}
              >
                <div style={{ gridColumn: '1 / -1' }}>
                  <span
                    style={{
                      fontSize: '0.95rem',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    Likelihood of attending
                  </span>
                  <IonSegment
                    value={attendanceLikelihood}
                    disabled={!canSubmit}
                    onIonChange={e =>
                      setAttendanceLikelihood(
                        e.detail.value as string | undefined
                      )
                    }
                  >
                    <IonSegmentButton value="0.25">
                      <IonLabel>25%</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="0.5">
                      <IonLabel>50%</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="0.75">
                      <IonLabel>75%</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="1">
                      <IonLabel>100%</IonLabel>
                    </IonSegmentButton>
                  </IonSegment>
                </div>

                <IonCheckbox
                  checked={smsReminder}
                  disabled={!canSubmit}
                  onIonChange={e => setSmsReminder(e.detail.checked)}
                  style={{ justifySelf: 'center' }}
                />
                <div>
                  <span style={{ fontSize: '0.95rem' }}>
                    Send me an SMS message to remind me about the topic a week
                    before and the day before.
                  </span>
                  <p
                    style={{
                      fontSize: pxToRem(14),
                      color: 'var(--ion-color-medium)',
                      marginTop: '8px',
                    }}
                  >
                    By checking this box, you agree to receive a one-time SMS
                    from Snowball. Message/data rates apply.
                  </p>
                </div>
              </div>
              <ActionGuard
                disableMode="locked"
                action={primaryAction}
                resource={RESOURCES.TOPIC_REGISTRATIONS}
                resourceId={primaryResourceId}
                helpText={{
                  singleVerification:
                    'Verify your phone number to register for this topic',
                  updateProfile:
                    'Update your profile to register for this topic',
                  setupProfile:
                    'Set up your profile to register for this topic',
                }}
                guardedElem={
                  <IonButton
                    expand="block"
                    onClick={
                      registration
                        ? handleUpdateRegistration
                        : handleRegisterForTopic
                    }
                    disabled={registering || updating}
                    color="success"
                    data-testid={
                      registration ? 'update-button' : 'register-button'
                    }
                  >
                    {registering || updating ? (
                      <IonSpinner name="crescent" />
                    ) : (
                      <IonIcon icon={checkmarkCircleOutline} />
                    )}
                    {registration
                      ? updating
                        ? 'Updating...'
                        : 'Update'
                      : registering
                        ? 'Registering...'
                        : 'Register for Topic'}
                  </IonButton>
                }
              />
              <p>You can change your responses later if your plans change.</p>
              {registration && (
                <ActionGuard
                  disableMode="locked"
                  action={ACTIONS.DELETE}
                  resource={RESOURCES.TOPIC_REGISTRATIONS}
                  resourceId={String(registration.id)}
                  helpText={{
                    singleVerification:
                      'Verify yourself to unregister from this topic',
                    updateProfile:
                      'Update your profile to unregister from this topic',
                    setupProfile:
                      'Set up your profile to unregister from this topic',
                  }}
                  guardedElem={
                    <IonButton
                      expand="block"
                      fill="outline"
                      color="danger"
                      disabled={unregistering}
                      onClick={() => setShowUnregisterAlert(true)}
                      data-testid="unregister-button"
                    >
                      {unregistering ? 'Unregistering...' : 'Unregister'}
                    </IonButton>
                  }
                />
              )}
            </ActionGuardContainer>
          </div>
        )}

        {topic && (
          <IonAlert
            isOpen={showSuccessAlert}
            onDidDismiss={() => setShowSuccessAlert(false)}
            header="Successfully Registered!"
            message={`You have successfully registered for the "${topic.title}" topic!`}
            buttons={[
              {
                text: 'OK',
                handler: () => {
                  setShowSuccessAlert(false);
                },
              },
            ]}
          />
        )}

        <IonAlert
          isOpen={showUnregisterAlert}
          onDidDismiss={() => setShowUnregisterAlert(false)}
          header="Unregister from topic?"
          message="Your attendance response will be deleted. You can register again later."
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Unregister',
              role: 'destructive',
              handler: () => {
                handleUnregister();
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default TopicRegistration;
